<template>
  <div ref="dropZoneRef" class="tw-relative" :data-test-id="dataTestId">
    <drop-zone-disabled-index v-if="props.disabled" />
    <div
      v-else
      role="button"
      class="tw-w-full tw-h-48 tw-rounded-md tw-bg-primary-grey-50 tw-border-dashed tw-border-primary-grey-500 tw-border-2 hover:tw-cursor-pointer tw-p-4 tw-flex tw-flex-col tw-justify-center tw-items-center hover:tw-bg-primary-grey-100 tw-transition-colors"
      :class="{ 'tw-bg-primary-grey-100': isOverDropZone }"
      @click="open()"
    >
      <div class="tw-relative tw-stroke-primary-grey-500 tw-mb-1">
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.1739 28.416C9.09438 28.416 7.10001 27.6258 5.62956 26.2193C4.1591 24.8128 3.33301 22.9051 3.33301 20.916C3.33301 18.9269 4.1591 17.0192 5.62956 15.6127C7.10001 14.2062 9.09438 13.416 11.1739 13.416C11.6651 11.228 13.1019 9.30515 15.1682 8.07054C16.1914 7.45922 17.3383 7.03526 18.5435 6.82287C19.7488 6.61048 20.9887 6.61381 22.1924 6.83268C23.3962 7.05155 24.5403 7.48167 25.5594 8.09848C26.5785 8.71529 27.4526 9.50671 28.1319 10.4276C28.8111 11.3484 29.2822 12.3806 29.5182 13.4653C29.7541 14.55 29.7504 15.6659 29.5073 16.7493H31.1739C32.721 16.7493 34.2047 17.3639 35.2987 18.4579C36.3927 19.5519 37.0073 21.0356 37.0073 22.5827C37.0073 24.1298 36.3927 25.6135 35.2987 26.7075C34.2047 27.8014 32.721 28.416 31.1739 28.416H29.5073M14.5072 23.416L19.5072 18.416M19.5072 18.416L24.5072 23.416M19.5072 18.416V33.416"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <div class="tw-relative tw-text-primary-grey-700 tw-font-bold">
        {{ $t("add-logo") }}
      </div>
      <div class="tw-relative tw-text-primary-grey-700 tw-font-normal">
        {{ $t("logo-file-guidelines") }}
      </div>
    </div>
  </div>
  <div
    v-if="fileAddErrorStatus.type !== ERROR_NONE"
    class="tw-text-system-error-500 tw-text-sm"
  >
    <div class="tw-relative tw-flex tw-items-center">
      <div class="tw-text-system-error-500 tw-mr-2">
        <!-- original was 30px; nearest supported size is 32 -->
        <cb-icon-warning :width="32" />
      </div>
      <div
        v-if="fileAddErrorStatus.type === ERROR_FILE_PARSE"
        class="tw-font-medium"
      >
        {{ $t("add-logo-failed") }}
      </div>
      <div
        v-if="fileAddErrorStatus.type === ERROR_FILE_TYPE"
        v-dompurify-html="
          $t('logo-file-format', {
            FileName: fileAddErrorStatus.fileName,
          })
        "
        class="tw-font-medium"
      ></div>
      <div
        v-if="fileAddErrorStatus.type === ERROR_FILE_SIZE"
        v-dompurify-html="
          $t('logo-file-size', {
            FileName: fileAddErrorStatus.fileName,
          })
        "
        class="tw-font-medium"
      ></div>
      <div
        v-if="fileAddErrorStatus.type === ERROR_FILE_DIMENSIONS"
        v-dompurify-html="$t('logo-dimensions')"
        class="tw-font-medium"
      ></div>
    </div>
  </div>
</template>

<script lang="ts">
export const ERROR_NONE = "ERROR_NONE";
export const ERROR_FILE_PARSE = "ERROR_FILE_PARSE";
export const ERROR_FILE_TYPE = "ERROR_FILE_TYPE";
export const ERROR_FILE_SIZE = "ERROR_FILE_SIZE";
export const ERROR_FILE_DIMENSIONS = "ERROR_FILE_DIMENSIONS";

export type ErrorNone = { type: typeof ERROR_NONE; fileName: string };
export type ErrorFileParse = {
  type: typeof ERROR_FILE_PARSE;
  fileName: string;
};
export type ErrorFileType = { type: typeof ERROR_FILE_TYPE; fileName: string };
export type ErrorFileSize = {
  type: typeof ERROR_FILE_SIZE;
  fileName: string;
};
export type ErrorFileDimensions = {
  type: typeof ERROR_FILE_DIMENSIONS;
  fileName: string;
};

export type FileAddErrorStatus =
  | ErrorNone
  | ErrorFileParse
  | ErrorFileType
  | ErrorFileSize
  | ErrorFileDimensions;
</script>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";
import { useDropZone, useFileDialog } from "@vueuse/core";
import DropZoneDisabledIndex from "./drop-zone-disabled/drop-zone-disabled.index.vue";

const props = defineProps<{
  disabled?: boolean;
  mode: "light" | "dark";
  dataTestId?: string;
}>();

const emit = defineEmits<{
  (event: "file-added", value: { file: File; mode: "light" | "dark" }): void;
}>();

const MAX_FILE_SIZE = 2; // Mb
const IMAGE_SVG = "image/svg+xml";
const IMAGE_PNG = "image/png";
const IMAGE_WEBP = "image/webp";
const IMAGE_MAX_W = 380;
const IMAGE_MAX_H = 100;
const ALLOWED_TYPES = [IMAGE_SVG, IMAGE_PNG, IMAGE_WEBP];
const shouldCheckProportions = false;

const fileAddErrorStatus = ref<FileAddErrorStatus>({
  type: ERROR_NONE,
  fileName: "",
});

const addFile = async (file: File | undefined) => {
  if (!file) {
    fileAddErrorStatus.value = { type: ERROR_FILE_PARSE, fileName: "" };
    return;
  }

  const fileSizeInMB = file.size / 1024 ** 2;

  if (!ALLOWED_TYPES.includes(file.type)) {
    fileAddErrorStatus.value = { type: ERROR_FILE_TYPE, fileName: file.name };
    return;
  }

  if (fileSizeInMB > MAX_FILE_SIZE) {
    fileAddErrorStatus.value = { type: ERROR_FILE_SIZE, fileName: file.name };
    return;
  }

  const { valid } = await checkImageDimension(file);
  if (!valid) {
    fileAddErrorStatus.value = {
      type: ERROR_FILE_DIMENSIONS,
      fileName: file.name,
    };
    return;
  }

  emit("file-added", { file, mode: props.mode });

  return;
};

const dropZoneRef = useTemplateRef<HTMLElement>("dropZoneRef");
const onDrop = (files: File[] | null) => {
  if (props.disabled) {
    return;
  }
  fileAddErrorStatus.value = { type: ERROR_NONE, fileName: "" };
  addFile(files?.shift());
};

const checkImageDimension = (file: File) => {
  return new Promise<{ valid: boolean; width: number; height: number }>(
    (resolve) => {
      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          const isWithinMaxSize = width <= IMAGE_MAX_W && height <= IMAGE_MAX_H;
          const aspectRatio = width / height;
          const isCorrectProportion = shouldCheckProportions
            ? Math.abs(aspectRatio - 190 / 50) < 0.1
            : true;
          resolve({
            valid: isCorrectProportion && isWithinMaxSize,
            width,
            height,
          });
        };
        img.onerror = () => resolve({ valid: false, width: 0, height: 0 });
        img.src = reader.result as string;
      };

      reader.onerror = () => resolve({ valid: false, width: 0, height: 0 });
      reader.readAsDataURL(file);
    },
  );
};

const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);
const { open, onChange } = useFileDialog({
  accept: ALLOWED_TYPES.join(","),
  multiple: false,
});

onChange((fileList) => {
  fileAddErrorStatus.value = { type: ERROR_NONE, fileName: "" };
  addFile(Object.values(fileList || {}).shift());
});
</script>
